/* eslint-disable react/prop-types */
/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useState } from 'react';

import appConfig from 'app-config';
import PropTypes from 'prop-types';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import { QuestionSetComponent, platformMessages } from 'ml-components-platform-react';
import { MLAddressDetailsComponent } from 'ml-components-common-react';
import '@xengage/gw-platform-translations';
import _ from 'lodash';

import messages from './BusinessDetailsPopupComponent.messages';
import metadata from './BusinessDetailsPopupComponent.metadata.json5';
import styles from './BusinessDetailsPopupComponent.module.scss';

// ML: MLBankAccountDetailsDirectiveCtrl.js, ml-bank-account-details.html
function BusinessDetailsPopupComponent(props) {
    const translator = useTranslator();

    const {
        id,
        onValidate,
        showValidationErrors,
        businessActivityVM,
        submissionVM,
        pupModel,
        onModelChange,
        syncExposure,
        onSave,
        getUnderlyingPolicies,
        createUnderlyingPolicy,
        isEndorsement,
        showProgressDialog,
        onCancel,
        onSaveBizActivity
    } = props;

    const [showErrors, updateShowErrors] = useState(showValidationErrors);
    const [hasQuestionError, setHasQuestionError] = useState(false);
    const underlyingPolicies = pupModel.underlyingPolicies.value;
    let isInSync = false;
    const exposureType = 'business';
    const { refreshData } = useDataRefresh();
    const isAmp = appConfig.portalCode === 'AMP';
    const isPersonalUmbrella = submissionVM.value.isPersonalUmbrella();
    const isModernProduct = _.get(submissionVM, 'isModernProduct_Ext.value', false);
    const isUMB_NP = isPersonalUmbrella && isModernProduct;

    const getAvailableClassTypes = (businessActivity) => {
        let availableValues = businessActivity.businessClass.aspects.availableValues;
        const excludedCodes = ['BusinessPursuits', 'IncidentalOccupancies'];

        if (isUMB_NP) {
            availableValues = availableValues.filter(
            (type) => !excludedCodes.includes(type.code)
            );
        } else {
            availableValues = availableValues.filter(
            (type) => excludedCodes.includes(type.code)
            );
        }
        return availableValues.map((type) => ({
            code: type.code,
            name: translator({
            id: type.name,
            defaultMessage: type.name
            })
        }));
    };

    const getAvailableBusinessTypes = (businessActivity) => {
        const availableValues = businessActivity.availableBusinessTypes.value || [];

        let types = [];
        if (businessActivity.businessType.aspects && businessActivity.businessType.aspects.availableValues) {
            types = businessActivity.businessType.aspects.availableValues.filter((type) => {
                return availableValues.indexOf(type.code) > -1;
            });
        }

        return types.map((type) => {
            return {
                code: type.code,
                name: translator({
                    id: type.name,
                    defaultMessage: type.name
                })
            };
        });
    };

    const getAvailableDescriptionTypes = (businessActivity) => {
        const availableValues = businessActivity.availableDescriptionTypes.value || [];

        let types = [];
        if (businessActivity.description.aspects && businessActivity.description.aspects.availableValues) {
            types = businessActivity.description.aspects.availableValues.filter((type) => {
                return availableValues.indexOf(type.code) > -1;
            });
        }

        return types.map((type) => {
            return {
                code: type.code,
                name: translator({
                    id: type.name,
                    defaultMessage: type.name
                })
            };
        });
    };

    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const addNewUnderlyingPolicyIfNew = () => {
        if(createUnderlyingPolicy){
            Promise.resolve(createUnderlyingPolicy(exposureType)).then((model) => {
                let val;
                if(model){
                    val = model.ulPolicyFixID.value;
                }
                businessActivityVM.ulPolicyFixID.value = val;
                onModelChange(businessActivityVM);
                refreshData();
            })
        }
    };

    const performSync = () => {
        businessActivityVM.value.isSelected = true;

        if (isInSync === false) {
            isInSync = true;
            const syncExposurePromise = syncExposure(businessActivityVM.publicID.value);
            showProgressDialog(platformMessages.syncing, syncExposurePromise);
            syncExposurePromise.then((businessActivity) => {
                _.set(businessActivityVM, 'value', businessActivity.value);
                // setScopeVariables(businessActivityVM);
                businessActivityVM.value.isEditing = true;
                isInSync = false;
                onModelChange(businessActivityVM);
            });
        }
    };

    const clearSelectedFields = (isBusinessClassChange) => {
        if (isBusinessClassChange && businessActivityVM.businessType.value !== undefined) {
            businessActivityVM.businessType.value = undefined;
        }
        if (businessActivityVM.description.value !== undefined) {
            businessActivityVM.description.value = undefined;
        }
        if (businessActivityVM.name.value !== undefined) {
            businessActivityVM.name.value = undefined;
        }
        if (businessActivityVM.ulPolicyFixID.value !== undefined) {
            businessActivityVM.ulPolicyFixID.value = undefined;
        }
        if (businessActivityVM.businessActivityQuestionSet && businessActivityVM.businessActivityQuestionSet.value !== undefined) {
            businessActivityVM.businessActivityQuestionSet.value.forEach((questionSet) => {
                if (questionSet && questionSet.answers) {
                    Object.keys(questionSet.answers).forEach((answer) => {
                        questionSet.answers[answer] = undefined;
                    });
                }
            });
        }
    };

    const onSaveDetails = useCallback(() => {
        businessActivityVM.toSync.value = false;
        businessActivityVM.isModernProduct.value = isModernProduct;
        if(isUMB_NP) {
            businessActivityVM.isModernProduct.value = true;
            const answers = businessActivityVM.businessActivityQuestionSet?.value?.[0]?.answers;
            const invalidAnswer = answers && Object.values(answers).some((value)=> value === null || value === undefined);
            setHasQuestionError(invalidAnswer);

            if (invalidAnswer   ) {
                updateShowErrors(true);
                return;
            }
        }
        if (!(businessActivityVM.aspects.valid && businessActivityVM.aspects.subtreeValid)) {
            updateShowErrors(true);
        }
        const shouldClosePopup = onSaveBizActivity();
        if (onSave) {
            onSave(shouldClosePopup);
        };
    }, [businessActivityVM, onSave, onSaveBizActivity]);

     const onValueChange = (value, path) => {
        switch (path) {
            case 'businessClass': {
                    businessActivityVM.toSync.value = true;
                    if (businessActivityVM.aspects.valid && businessActivityVM.aspects.subtreeValid) {
                    clearSelectedFields(true);
                    performSync();
                }   
                break;
            }
            case 'businessType': {
                businessActivityVM.toSync.value = true;
                if (businessActivityVM.aspects.valid && businessActivityVM.aspects.subtreeValid) {
                    clearSelectedFields(false);
                    performSync();
                }     
                break;
            }
            case 'ulPolicyFixID': {
                if (businessActivityVM.ulPolicyFixID.value === 'new') {
                    addNewUnderlyingPolicyIfNew();
                }
                break;
            }
            case 'onPremises': {
                if (businessActivityVM.onPremises.value === false) {
                    businessActivityVM.address.value = {};
                }
                break;
            }
            default:
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: { questionset: QuestionSetComponent,
            mladdressdetailscomponent: MLAddressDetailsComponent
         },
        resolveCallbackMap: {
            onSaveDetails: onSaveDetails,
            onCancel: onCancel
        }
    };

    const getQuestionSetHeaderContent = (businessActivity) => {
      
        return (_.get(businessActivity, 'businessClass.value.code')  === 'IncidentalOccupancies') ? 
            translator(messages.AddBusinessActivityPopupIncidental): 
            translator(messages.AddBusinessActivityPopupBusiness)
        
    };
    const getUnderlyingPoliciesLable=()=>{
        if(isAmp){
            return translator(messages.MyPolicyInfoLabel)
        }
        return translator(messages.UnderlyingPolicyLabel) 
    }

    const validateAndSyncExposure = () => {
            const isValid = businessActivityVM.aspects.valid && businessActivityVM.aspects.subtreeValid;
            if (isValid) {
                businessActivityVM.value.isSelected = true;
                const syncExposurePromise = syncExposure(businessActivityVM.value);
                showProgressDialog(platformMessages.syncing, syncExposurePromise);
                syncExposurePromise.then((businessActivity) => {
                    _.set(businessActivityVM, 'value', businessActivity.value);
                    businessActivityVM.value.isEditing = true;
                    isInSync = false;
                    onModelChange(businessActivityVM);
                });
            }
    };

    const syncAddressChange = (value) => {
        businessActivityVM.address.value = value;
        if (isEndorsement) {
            validateAndSyncExposure();
        } else if (businessActivityVM.aspects.valid && businessActivityVM.aspects.subtreeValid && !businessActivityVM.businessActivityQuestionSet.value) {
            performSync();
        }
        businessActivityVM.address.validateAddressStatus_ML.value = false;
        onModelChange(businessActivityVM);
    };

    const overrides = {
        businessDetailsContainer: {
            className: isEndorsement ? undefined : styles.businessDetailsContainer 
        },
        underlyingPoliciesSection: {
            visible: isEndorsement ? true : (underlyingPolicies && underlyingPolicies.length > 0)
        },
        questionSetH2: {
            visible: businessActivityVM.businessActivityQuestionSet.value !== undefined && businessActivityVM.businessActivityQuestionSet.value.length > 0,
            content: isUMB_NP ? translator(messages.AddBusinessActivityPopupBusinessQuestionsetHeader) : getQuestionSetHeaderContent(businessActivityVM)
        },
        businessactivityquestionset: {
            visible: businessActivityVM.businessActivityQuestionSet.value !== undefined,
            labelPosition: 'top',
            onValidate: onValidate,
            showErrors: showValidationErrors,
            showHeaderML: false,
            validationMessage: hasQuestionError ? translator(messages.RequiredQuestion) : undefined
            // onValueChange: syncQuestionSets
        },
        businessClass: {
            availableValues: getAvailableClassTypes(businessActivityVM),
            value: businessActivityVM.businessClass.value
        },
        businessType: {
            availableValues: getAvailableBusinessTypes(businessActivityVM),
            value: businessActivityVM.businessType.value,
            visible: !isUMB_NP || (isUMB_NP && businessActivityVM.businessClass?.value?.code === 'ExcessBusPursuit')
        },
        businessDescription: {
            availableValues: getAvailableDescriptionTypes(businessActivityVM),
            value: businessActivityVM.description.value,
            visible: !isUMB_NP
        },
        underlyingPolicies: {
            label: getUnderlyingPoliciesLable(),
            availableValues: getUnderlyingPolicies(),
            value: businessActivityVM.ulPolicyFixID.value
        },
        actionRow: {
            visible: !isEndorsement
        },
        onPremisesSection: {
            visible: businessActivityVM.businessClass?.value?.code === 'ExcessHOBus'
        },
        addressDetailsComponent: {
            addressVM: businessActivityVM.address,
            onModelChange: syncAddressChange,
            layout: 'ml-policy-change',
            onValidate: onValidate,
            showValidationErrors: showErrors
        },
        addressRow: {
            visible: businessActivityVM?.onPremises.value == false
        },
        onPremisesLabel: {
            visible: businessActivityVM.businessClass?.value?.code === 'ExcessHOBus'
        },
        businessName: {
            visible: !isUMB_NP || isUMB_NP && (businessActivityVM.businessClass?.value?.code === 'ExcessBusPursuit' || businessActivityVM.businessClass?.value?.code === 'ExcessHOBus')
        },
        businessClassComponent: {  
            tooltipContent: isUMB_NP ? messages.BusinessTypeTooltipContentForNP : messages.BusinessTypeTooltipContent
        },
    };


    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            model={businessActivityVM}
            onValidationChange={setComponentValidation}
            showErrors={showErrors || showValidationErrors}
            onModelChange={onModelChange}
            onValueChange={onValueChange}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

BusinessDetailsPopupComponent.propTypes = {
    id: PropTypes.string,
    onValidate: PropTypes.func,
    showValidationErrors: PropTypes.bool,
    pupModel: PropTypes.shape({}).isRequired,
    businessActivityVM: PropTypes.shape({}).isRequired,
    onModelChange: PropTypes.func.isRequired,
    syncExposure: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    getUnderlyingPolicies: PropTypes.func.isRequired,
    createUnderlyingPolicy: PropTypes.func,
    isEndorsement: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onSaveBizActivity: PropTypes.func.isRequired
};

BusinessDetailsPopupComponent.defaultProps = {
    id: undefined,
    onValidate: undefined,
    showValidationErrors: undefined,
    isEndorsement: false
};

export default BusinessDetailsPopupComponent;
